import { useState } from 'react';
import NextLink from 'next/link';
import useSolutions from 'lib/hooks/useSolutions';
import { tv } from 'tailwind-variants';
import { useRouter } from 'next/router';

import { selectCurrentUser } from 'features/auth/slice';
import { useDispatch, useSelector } from 'react-redux';
import { updateScore } from 'features/learners/slice';

import {
  Button,
  flash,
  formatTimeAgo,
  Heading,
  Icon,
  Image,
  LinkWithButtonStyles,
  LoadingScreen,
  PopoverMenu,
  recordsMatch,
} from '@frontendmentorio/design-system-v2';

import AdminDeleteButton from 'features/shared/AdminDeleteButton';
import BookmarkButton from 'features/solutions/BookmarkButton';
import ConfirmPopoverMenuItem from 'features/shared/ConfirmPopoverMenuItem';
import LikeButton from 'features/solutions/LikeButton';
import Share from 'features/solutions/Share';
import UserDetails from 'features/learners/UserDetails';

const buttonStyles = tv({
  extend: Button.styles,
  base: [
    'bg-white',
    'border',
    'border-neutral-200',
    'gap-2',
    'px-4',
    '[&>*:last-child]:hidden',
    'disabled:bg-white',
    'disabled:border-neutral-200',
    'hover:bg-neutral-50',
    'tablet:px-5',
    'tablet:[&>*:last-child]:block',
  ],
});

export default function Hero({ solution }) {
  const { remove } = useSolutions();

  const dispatch = useDispatch();
  const router = useRouter();
  const [isLoading, setIsLoading] = useState();
  const currentUser = useSelector(selectCurrentUser);

  const isAuthor = recordsMatch(currentUser, solution.user);

  const handleDeleteSolution = async () => {
    setIsLoading(true);

    try {
      await remove(solution);
      await dispatch(updateScore(solution.challenge.difficulty * -10));

      if (!isAuthor) return await router.push('/solutions');
      return await router.push('/home/my-challenges');
    } catch (error) {
      flash.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) return <LoadingScreen />;

  return (
    <>
      <div className="container-xl flex flex-col-reverse gap-6 px-5 py-7 tablet:flex-row tablet:items-center">
        <div className="flex flex-col gap-4 tablet:min-w-[25rem] tablet:w-1/2">
          <span className="text-neutral-500 text-sm">
            Submitted {formatTimeAgo(solution.submittedAt)}
          </span>

          <Heading className="w-full" level="4" tagName="h1">
            {solution.title}
          </Heading>

          {solution?.tags.length > 0 && (
            <div className="flex gap-3 items-center text-neutral-500">
              <Icon className="text-lg" name="tags" />

              <span>{solution.tags.map((tag) => tag.name).join(', ')}</span>
            </div>
          )}

          <UserDetails user={solution.user} />

          {isAuthor && (
            <div className="flex gap-3">
              <Share challenge={solution.challenge} solution={solution} />

              <PopoverMenu
                buttonClassName="border border-neutral-200 text-xl w-[3rem]"
                hasBackground
                icon="kebab"
                name="solution options"
              >
                <PopoverMenu.Item href={`/solutions/${solution.slug}/edit`}>
                  Edit solution
                </PopoverMenu.Item>

                <ConfirmPopoverMenuItem
                  className="text-rose-600"
                  modal={{
                    button: 'Delete solution',
                    description:
                      'This will permanently delete your solution **and remove any awarded points** for submitting this solution from your Mentor Score.',
                  }}
                  onClick={handleDeleteSolution}
                >
                  Delete solution
                </ConfirmPopoverMenuItem>
              </PopoverMenu>
            </div>
          )}

          <span className="text-neutral-500">
            A solution to the{' '}
            <NextLink
              className="font-bold font-secondary italic text-blue-500 underline uppercase hover:no-underline"
              href={`/challenges/${solution.challenge.slug}`}
            >
              {solution.challenge.title}
            </NextLink>{' '}
            challenge
          </span>
        </div>

        <div className="aspect-[4/3] max-h-[24rem] overflow-hidden relative rounded-xl w-full tablet:w-1/2">
          {currentUser?.admin && !isAuthor && (
            <AdminDeleteButton
              className="absolute border right-[1rem] top-[1rem]"
              modal={{
                button: 'Delete solution',
                description:
                  'This will permanently delete this solution **and remove any associated points**.',
                title: 'One last check! 😅',
              }}
              onClick={handleDeleteSolution}
            />
          )}

          <Image
            alt=""
            className="h-full object-cover object-top w-full"
            height={300}
            priority
            src={solution.screenshot}
            width={500}
          />
        </div>
      </div>

      <div className="bg-gray-100 border-neutral-200 border-y p-4 sticky top-0 z-30">
        <div className="container-xl flex flex-wrap gap-3 items-center justify-between mobile:flex-row">
          <div className="flex gap-3">
            <LinkWithButtonStyles
              className="gap-2 px-4 tablet:px-5"
              href={solution.liveURL}
              target="_blank"
            >
              <span className="hidden tablet:block">View live site</span>
              <span className="tablet:hidden">Preview</span>
              <span className="sr-only"> (opens in new tab)</span>
              <Icon name="arrow-up-right-from-square" style="solid" />
            </LinkWithButtonStyles>

            <LinkWithButtonStyles
              className="border-neutral-200 gap-2 px-4 hover:border-neutral-200 mobile:w-auto tablet:px-5"
              href={solution.repoURL}
              target="_blank"
              level="tertiary"
            >
              <span className="hidden tablet:block">View code</span>
              <span className="tablet:hidden">Code</span>
              <span className="sr-only"> (opens in new tab)</span>
              <Icon name="arrow-up-right-from-square" style="solid" />
            </LinkWithButtonStyles>
          </div>

          <div className="flex gap-3">
            <LikeButton className={buttonStyles()} solution={solution} />

            <BookmarkButton className={buttonStyles()} solution={solution} />
          </div>
        </div>
      </div>
    </>
  );
}
